import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  useTheme, 
  alpha,
  Container,
  Divider 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HomeIcon from '@mui/icons-material/Home';
import DescriptionIcon from '@mui/icons-material/Description';

const LoanApplicationSuccess = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Generate a random application ID
  const applicationId = `LOAN-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
  
  return (
    <Container maxWidth="md">
      <Box sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            boxShadow: `0 10px 40px ${alpha(theme.palette.success.main, 0.1)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: theme.palette.success.main
            }
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 80,
              color: theme.palette.success.main,
              mb: 2
            }}
          />
          
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700, 
              mb: 2,
              color: theme.palette.success.dark 
            }}
          >
            Application Submitted Successfully!
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4,
              fontSize: '1.1rem',
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Your loan application has been received and is now being processed. You will be notified about the status updates via email and SMS.
          </Typography>
          
          <Box
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.05),
              py: 3,
              px: 4,
              borderRadius: 2,
              mb: 4,
              display: 'inline-block'
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Application Reference ID:
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                letterSpacing: 1,
                color: theme.palette.primary.main
              }}
            >
              {applicationId}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 4 }} />
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              What happens next?
            </Typography>
            
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                maxWidth: 500,
                mx: 'auto',
                textAlign: 'left'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.primary.main,
                    color: '#fff',
                    mr: 2,
                    fontWeight: 700,
                    flexShrink: 0
                  }}
                >
                  1
                </Box>
                <Typography variant="body1">
                  Our team will review your application within 24-48 hours.
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.primary.main,
                    color: '#fff',
                    mr: 2,
                    fontWeight: 700,
                    flexShrink: 0
                  }}
                >
                  2
                </Box>
                <Typography variant="body1">
                  You'll receive a notification once your application has been reviewed.
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.primary.main,
                    color: '#fff',
                    mr: 2,
                    fontWeight: 700,
                    flexShrink: 0
                  }}
                >
                  3
                </Box>
                <Typography variant="body1">
                  Our representative may contact you for additional information if required.
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{ 
                px: 3, 
                py: 1.2,
                borderRadius: 2
              }}
            >
              Back to Dashboard
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<DescriptionIcon />}
              onClick={() => navigate('/applications')}
              sx={{ 
                px: 3, 
                py: 1.2,
                borderRadius: 2
              }}
            >
              View My Applications
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoanApplicationSuccess; 